import { type DesktopPlatform, DesktopSystem } from '@boardflight/shared';
import { downloadUrl, manifest } from '../../downloads';

/**
 * Whether there is a desktop app to download — the one fact the website's onboarding turns on
 * (`docs/off-board-plan.md` §5.6, §5.7).
 *
 * **The website describes only what exists.** A build of the app is archived on every deploy and
 * is not a release; it becomes one when an operator promotes it, and the pointer that says so is
 * `desktop/latest.json` in the download bucket (`scripts/promote-desktop.mjs`). Until that file
 * exists there is no app as far as this page is concerned: *Get the app* and *Waiting* are not
 * steps, and no sentence anywhere mentions one. The day it is promoted they appear by themselves,
 * with no deploy of this site.
 *
 * Read the way every other published manifest is read (`downloads.ts`): a cross-origin GET the
 * bucket's CORS rule allows from this origin. A file that is not there, a network that is down and
 * a body that is not the expected shape are all the same answer — nothing released — because each
 * means the same thing to a visitor: there is nothing here to press.
 */

/** One installer, as `scripts/desktop-manifest.mjs` recorded it. */
export interface DesktopBuild {
  platform: DesktopSystem;
  /** `macOS`, `Windows (MSI)`, `Linux (.deb)` — the manifest's own words. */
  label: string;
  file: string;
  url: string;
  size: number | null;
}

export interface DesktopRelease {
  version: string;
  builds: DesktopBuild[];
}

export const DESKTOP_RELEASE_PATH = 'desktop/latest.json';

/**
 * The manifest, read as a release — or null for anything that is not one.
 *
 * An installer is kept only when its address is under this site's own download origin. The
 * manifest is ours, so that is always true of a real one; the check is there so that a file that
 * ever said otherwise could not put somebody else's download behind this page's button.
 */
export function parseDesktopRelease(raw: unknown, origin: string = downloadUrl('')): DesktopRelease | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const { version, builds } = raw as { version?: unknown; builds?: unknown };
  if (typeof version !== 'string' || version.trim() === '' || !Array.isArray(builds)) return null;
  const kept: DesktopBuild[] = [];
  for (const entry of builds as unknown[]) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { platform, label, file, url, size } = entry as Record<string, unknown>;
    const system = DesktopSystem.safeParse(platform);
    if (!system.success) continue;
    if (typeof url !== 'string' || !url.startsWith('https://') || !url.startsWith(origin)) continue;
    if (typeof file !== 'string' || file === '') continue;
    kept.push({
      platform: system.data,
      label: typeof label === 'string' && label.trim() !== '' ? label : system.data,
      file,
      url,
      size: typeof size === 'number' && Number.isFinite(size) && size > 0 ? size : null,
    });
  }
  return kept.length === 0 ? null : { version: version.trim(), builds: kept };
}

/**
 * The installers for one system, the one to offer first. Empty for a phone, for a system the
 * release was not built for, and for no release at all.
 *
 * The manifest lists files in name order, which is not an order of preference: on Windows the
 * setup program comes before the MSI an administrator might want, and on Linux the AppImage, which
 * runs anywhere, before the `.deb`.
 */
export function buildsFor(release: DesktopRelease | null, platform: DesktopPlatform | null): DesktopBuild[] {
  if (!release || !platform) return [];
  const rank = (build: DesktopBuild): number => (/\.(msi|deb)$/i.test(build.file) ? 1 : 0);
  return release.builds.filter((build) => build.platform === platform).sort((a, b) => rank(a) - rank(b));
}

let asked: Promise<DesktopRelease | null> | null = null;

/** The released desktop app, or null. Asked once per page load, however many screens want it. */
export function loadDesktopRelease(read: (path: string) => Promise<unknown> = manifest): Promise<DesktopRelease | null> {
  asked ??= read(DESKTOP_RELEASE_PATH).then(
    (body) => parseDesktopRelease(body),
    () => null,
  );
  return asked;
}

/** For tests: forget the answer, so the next caller asks again. */
export function forgetDesktopRelease(): void {
  asked = null;
}
